import type { CarePlan, Practitioner, Reference } from '@medplum/fhirtypes'
import { maxBy, sumBy } from 'lodash-es'
import type {
  BundleMutationBuilder,
  TransactionMutationBuilder,
} from '#src/bundle-builder.ts'
import { invariant } from '#src/invariant.ts'
import type { BasePlan } from '#src/models/care-plans/BasePlan.ts'
import { CarePlanSnapshot } from '#src/models/care-plans/CarePlanSnapshot.ts'
import { CarePlanState } from '#src/models/care-plans/constants.ts'
import type { GoalCarePlan } from '#src/models/care-plans/GoalCarePlan.ts'
import type { InitialCarePlan } from '#src/models/care-plans/InitialCarePlan.ts'
import type { Intervention } from '#src/models/care-plans/intervention.ts'
import { idtRoles } from '#src/models/care-teams/constants.ts'
import type { IDT } from '#src/models/care-teams/idt.ts'
import {
  ConditionVerificationStatus,
  Diagnosis,
  type ExtendedCondition,
} from '#src/models/conditions/index.ts'
import type { Outcome } from '#src/models/outcome.ts'
import type { QuestionnaireWithResponse } from '#src/models/questionnaireResponse.ts'
import type { CarePlanParticipantSignatureTask } from '#src/models/tasks/signature/carePlanParticipantSignatureTask.ts'
import { CarePlanSignatureTask } from '#src/models/tasks/signature/index.ts'
import { queryTypesToString } from '#src/search.ts'
import { hasReference } from '#src/utils.ts'

export function createCarePlanSignatures(
  transaction: BundleMutationBuilder,
  {
    carePlan,
    idt,
    requester,
  }: {
    carePlan: InitialCarePlan
    idt: IDT
    requester: Reference<Practitioner>
  }
): void {
  // One signature per IDT role, not per practitioner. A practitioner
  // double-rostered across two roles gets one task per role, so each discipline
  // can be signed independently (including on behalf of the assignee). All 11
  // roles are always created; an unstaffed role yields a task with no
  // owner, surfacing the missing assignment as a red flag.
  idtRoles.forEach((role) => {
    const member = idt.getMemberByRole(role)
    transaction.create(
      new CarePlanSignatureTask({
        carePlan,
        requester,
        role,
        signer: member?.practitioner,
      }),
      {
        request: {
          ifNoneExist: queryTypesToString(
            CarePlanSignatureTask.byRoles({
              role,
              carePlan: carePlan.reference,
            })
          ),
        },
      }
    )
  })
}

export function moveCarePlanSignatures(
  transaction: TransactionMutationBuilder,
  {
    sourceCarePlan,
    targetCarePlan,
    signatures,
  }: {
    sourceCarePlan: Reference<CarePlan>
    targetCarePlan: Reference<CarePlan>
    signatures: CarePlanSignatureTask[]
  }
): void {
  signatures.forEach((signature) => {
    transaction.patch(signature, (signature) => {
      invariant(
        hasReference(signature.carePlan, sourceCarePlan),
        `CarePlanSignatureTask ${signature.id} must be for the source care plan ${sourceCarePlan.id}`
      )
      signature.carePlan = targetCarePlan
    })
  })
}

export function deleteCarePlanGoal(
  bundle: TransactionMutationBuilder,
  { carePlan, goal }: { carePlan: BasePlan; goal: Outcome }
): void {
  bundle.patch(carePlan, (carePlan) => {
    carePlan.removeGoal(goal.reference)
  })
  bundle.delete(goal)
}

export function createCarePlanOutcome(
  bundle: TransactionMutationBuilder,
  { carePlan, outcome }: { carePlan: GoalCarePlan; outcome: Outcome }
): void {
  const outcomeRef = bundle.create(outcome)
  bundle.patch(carePlan, (gcp) => {
    gcp.addGoal(outcomeRef)
  })
}

export function deleteCarePlanOutcome(
  bundle: TransactionMutationBuilder,
  { carePlan, outcome }: { carePlan: GoalCarePlan; outcome: Outcome }
): void {
  bundle.patch(carePlan, (gcp) => {
    gcp.removeGoal(outcome.reference)
  })
  bundle.delete(outcome)
}

export function countConditionInCarePlans(
  carePlans: BasePlan[],
  condition: ExtendedCondition
): number {
  return sumBy(carePlans, (carePlan) =>
    carePlan.hasCondition(condition.reference) ? 1 : 0
  )
}

export function deleteCarePlanCondition(
  bundle: TransactionMutationBuilder,
  {
    carePlans,
    condition,
  }: { carePlans: BasePlan[]; condition: ExtendedCondition }
): void {
  for (const carePlan of carePlans) {
    bundle.patch(carePlan, (carePlan) => {
      carePlan.removeCondition(condition.reference)
    })
  }
  if (!Diagnosis.isInstance(condition)) {
    bundle.patch(condition, (condition) => {
      condition.verificationStatus =
        ConditionVerificationStatus.ENTERED_IN_ERROR
    })
  }
}

export function activateCarePlan(
  bundle: TransactionMutationBuilder,
  {
    author,
    carePlan,
    goalCarePlans,
    interventions,
    signatures,
    participantSignatures,
    demographics,
    currentActiveCarePlans,
  }: {
    author: Reference<Practitioner>
    carePlan: InitialCarePlan
    goalCarePlans: GoalCarePlan[]
    interventions: Intervention[]
    signatures: CarePlanSignatureTask[]
    participantSignatures: CarePlanParticipantSignatureTask[]
    demographics: QuestionnaireWithResponse[]
    currentActiveCarePlans: InitialCarePlan[]
  }
): void {
  const lastSignatureCompletionDate = maxBy(
    signatures.filter((s) => s.isDone),
    (s) => s.completionDate
  )?.completionDate
  invariant(
    lastSignatureCompletionDate,
    'Last signature completion date is required'
  )
  // The care plan's start/activation date is the date it was reviewed with the
  // participant (the 1-on-1 care plan review), not the date the IDT signed off.
  const participantReviewCompletionDate = maxBy(
    participantSignatures.filter((s) => s.isDone),
    (s) => s.completionDate
  )?.completionDate
  invariant(
    participantReviewCompletionDate,
    'Participant review completion date is required'
  )
  const signedOffCarePlanComposition = bundle.create(
    new CarePlanSnapshot({
      date: lastSignatureCompletionDate,
      author,
      initialCarePlan: carePlan,
      goalCarePlans,
      interventions,
      signatures,
      participantSignatures,
      demographics,
    })
  )
  demographics.forEach((demographic) => {
    if (
      demographic.questionnaireResponse &&
      demographic.questionnaireResponse.status === 'in-progress'
    ) {
      bundle.patch(demographic.questionnaireResponse, (qr) => {
        qr.status = 'completed'
      })
    }
  })
  bundle.patch(carePlan, (carePlan) => {
    carePlan.state = CarePlanState.Active
    carePlan.signedSnapshots = [signedOffCarePlanComposition]
    carePlan.startDate = participantReviewCompletionDate
  })
  currentActiveCarePlans.forEach((cp) => {
    bundle.patch(cp, (cp) => {
      cp.state = CarePlanState.Closed
    })
  })
}
